import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const SESSION_MS = 30 * 60 * 1000;
const POLL_MS = 30 * 1000;
const SESSION_KEY = "codeforcesTrainingSession";
const RATING_OPTIONS = Array.from({ length: 28 }, (_, i) => 800 + i * 100);

const roundRatingDown = (rating) => {
  if (!rating || rating < 800) return 800;
  return Math.floor(rating / 100) * 100;
};

const clampRating = (rating) => Math.max(800, Math.min(3500, rating));

const problemKey = (problem) => `${problem.contestId}-${problem.index}`;

const formatTime = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const getRatingBadge = (rating) => {
  if (rating < 1200) return "bg-gray-500 text-white";
  if (rating < 1400) return "bg-green-500 text-white";
  if (rating < 1600) return "bg-cyan-500 text-white";
  if (rating < 1900) return "bg-blue-500 text-white";
  if (rating < 2100) return "bg-purple-500 text-white";
  if (rating < 2400) return "bg-yellow-500 text-black";
  if (rating < 3000) return "bg-red-500 text-white";
  return "bg-red-700 text-white";
};

function Training({ username, problemsWithStats, availableTags = [], toggleDarkMode, isDarkMode }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("setup"); // setup | solving | review | complete
  const [selectedTag, setSelectedTag] = useState("any");
  const [tagSearch, setTagSearch] = useState("");
  const [selectedStartRating, setSelectedStartRating] = useState("auto"); // "auto" | number
  const [userRating, setUserRating] = useState(null);
  const [targetRating, setTargetRating] = useState(null);
  const [currentProblem, setCurrentProblem] = useState(null);
  const [usedKeys, setUsedKeys] = useState([]);
  const [history, setHistory] = useState([]);
  const [deadline, setDeadline] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [solvedCount, setSolvedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [loadingStart, setLoadingStart] = useState(false);
  const [pickingNext, setPickingNext] = useState(false);
  const [error, setError] = useState(null);
  const [lastPollAt, setLastPollAt] = useState(null);
  const [pollStatus, setPollStatus] = useState("idle");
  const [restored, setRestored] = useState(false);

  const advancingRef = useRef(false);
  const sessionRef = useRef({ phase, currentProblem, deadline, targetRating, usedKeys });
  const advanceAfterAcceptRef = useRef(null);

  useEffect(() => {
    sessionRef.current = { phase, currentProblem, deadline, targetRating, usedKeys };
  }, [phase, currentProblem, deadline, targetRating, usedKeys]);

  const remainingMs = deadline ? deadline - now : 0;
  const timerUrgent = remainingMs > 0 && remainingMs < 5 * 60 * 1000;

  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    const tags = [...availableTags].sort((a, b) => a.localeCompare(b));
    if (!q) return tags;
    return tags.filter((tag) => tag.toLowerCase().includes(q));
  }, [availableTags, tagSearch]);

  const pickProblem = useCallback(
    (rating, tag, excludeKeys = []) => {
      const exclude = new Set(excludeKeys);
      const candidates = (problemsWithStats || []).filter((problem) => {
        if (!problem.rating || problem.rating !== rating) return false;
        if (problem.isSolved) return false;
        if (exclude.has(problemKey(problem))) return false;
        if (tag && tag !== "any" && !(problem.tags || []).includes(tag)) return false;
        return true;
      });

      if (!candidates.length) return null;

      // Ease metric: prefer higher solve counts + higher relative solve-rate in this rating band.
      // (CF API only gives solvedCount; relative rate ≈ solved / maxSolved at this rating.)
      const maxSolves = Math.max(...candidates.map((p) => p.solvedCount || 0), 1);
      const scored = candidates.map((p) => {
        const solves = p.solvedCount || 0;
        const relativeRate = solves / maxSolves; // 0..1 among same-rating peers
        const absoluteSolves = Math.log10(solves + 10) / Math.log10(maxSolves + 10); // 0..1-ish
        const ease = relativeRate * 0.6 + absoluteSolves * 0.4;
        // Cube weight so training strongly leans easier / more standard problems
        return { p, w: Math.pow(Math.max(ease, 0.02), 3) };
      });

      const total = scored.reduce((s, x) => s + x.w, 0);
      let r = Math.random() * total;
      for (const item of scored) {
        r -= item.w;
        if (r <= 0) return item.p;
      }
      return scored[scored.length - 1].p;
    },
    [problemsWithStats]
  );

  const findNearestProblem = useCallback(
    (startRating, tag, excludeKeys = [], direction = "both") => {
      for (let delta = 0; delta <= 1500; delta += 100) {
        const ratingsToTry = [];
        if (delta === 0) {
          ratingsToTry.push(startRating);
        } else {
          if (direction === "both" || direction === "up") ratingsToTry.push(clampRating(startRating + delta));
          if (direction === "both" || direction === "down") ratingsToTry.push(clampRating(startRating - delta));
        }

        for (const rating of [...new Set(ratingsToTry)]) {
          const problem = pickProblem(rating, tag, excludeKeys);
          if (problem) return { problem, rating };
        }
      }
      return null;
    },
    [pickProblem]
  );

  const persistSession = useCallback(
    (payload) => {
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          username,
          selectedTag,
          userRating,
          ...payload,
          savedAt: Date.now(),
        })
      );
    },
    [username, selectedTag, userRating]
  );

  const clearSession = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
  }, []);

  const startRound = useCallback(
    (problem, rating, nextPhase, extras = {}) => {
      const key = problemKey(problem);
      const nextUsed = extras.usedKeys || [...usedKeys, key];
      const nextDeadline = Date.now() + SESSION_MS;

      setCurrentProblem(problem);
      setTargetRating(rating);
      setUsedKeys(nextUsed);
      setDeadline(nextDeadline);
      setPhase(nextPhase);
      setError(null);
      setPickingNext(false);
      advancingRef.current = false;

      persistSession({
        phase: nextPhase,
        currentProblem: problem,
        targetRating: rating,
        usedKeys: nextUsed,
        deadline: nextDeadline,
        history: extras.history || history,
        streak: extras.streak ?? streak,
        bestStreak: extras.bestStreak ?? bestStreak,
        solvedCount: extras.solvedCount ?? solvedCount,
        failedCount: extras.failedCount ?? failedCount,
        selectedTag: extras.selectedTag ?? selectedTag,
        userRating: extras.userRating ?? userRating,
      });
    },
    [usedKeys, history, streak, bestStreak, solvedCount, failedCount, selectedTag, userRating, persistSession]
  );

  // Restore session if present
  useEffect(() => {
    if (restored) return;
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) {
        setRestored(true);
        return;
      }
      const saved = JSON.parse(raw);
      if (saved.username !== username) {
        clearSession();
        setRestored(true);
        return;
      }
      if (saved.phase === "solving" || saved.phase === "review") {
        setSelectedTag(saved.selectedTag || "any");
        setUserRating(saved.userRating ?? null);
        setTargetRating(saved.targetRating ?? null);
        setCurrentProblem(saved.currentProblem ?? null);
        setUsedKeys(saved.usedKeys || []);
        setHistory(saved.history || []);
        setStreak(saved.streak || 0);
        setBestStreak(saved.bestStreak || 0);
        setSolvedCount(saved.solvedCount || 0);
        setFailedCount(saved.failedCount || 0);
        setDeadline(saved.deadline || Date.now() + SESSION_MS);
        setPhase(saved.phase);
      }
    } catch {
      clearSession();
    } finally {
      setRestored(true);
    }
  }, [username, restored, clearSession]);

  // Tick clock every second while active
  useEffect(() => {
    if (phase !== "solving" && phase !== "review") return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const advanceAfterAccept = useCallback(
    async (acceptedAt) => {
      if (advancingRef.current) return;
      advancingRef.current = true;

      const problem = sessionRef.current.currentProblem;
      if (!problem) {
        advancingRef.current = false;
        return;
      }

      const wasReview = sessionRef.current.phase === "review";
      const nextHistory = [
        {
          key: problemKey(problem),
          name: problem.name,
          rating: problem.rating,
          contestId: problem.contestId,
          index: problem.index,
          result: wasReview ? "reviewed" : "solved",
          at: acceptedAt || Date.now(),
        },
        ...history,
      ];

      const nextSolved = wasReview ? solvedCount : solvedCount + 1;
      const nextFailed = wasReview ? failedCount + 1 : failedCount;
      const nextStreak = wasReview ? 0 : streak + 1;
      const nextBest = Math.max(bestStreak, nextStreak);
      const nextRating = clampRating((sessionRef.current.targetRating || problem.rating || 800) + (wasReview ? -100 : 100));

      setHistory(nextHistory);
      setSolvedCount(nextSolved);
      setFailedCount(nextFailed);
      setStreak(nextStreak);
      setBestStreak(nextBest);
      setPickingNext(true);

      const found = findNearestProblem(nextRating, selectedTag, sessionRef.current.usedKeys, "both");
      if (!found) {
        setPhase("complete");
        setCurrentProblem(null);
        setDeadline(null);
        setPickingNext(false);
        clearSession();
        advancingRef.current = false;
        return;
      }

      startRound(found.problem, found.rating, "solving", {
        usedKeys: [...sessionRef.current.usedKeys, problemKey(found.problem)],
        history: nextHistory,
        streak: nextStreak,
        bestStreak: nextBest,
        solvedCount: nextSolved,
        failedCount: nextFailed,
      });
    },
    [history, solvedCount, failedCount, streak, bestStreak, selectedTag, findNearestProblem, startRound, clearSession]
  );

  useEffect(() => {
    advanceAfterAcceptRef.current = advanceAfterAccept;
  }, [advanceAfterAccept]);

  const enterReview = useCallback(() => {
    if (advancingRef.current || sessionRef.current.phase !== "solving") return;
    const nextDeadline = Date.now() + SESSION_MS;
    setPhase("review");
    setDeadline(nextDeadline);
    persistSession({
      phase: "review",
      currentProblem,
      targetRating,
      usedKeys,
      deadline: nextDeadline,
      history,
      streak,
      bestStreak,
      solvedCount,
      failedCount,
    });
  }, [currentProblem, targetRating, usedKeys, history, streak, bestStreak, solvedCount, failedCount, persistSession]);

  // When solve timer expires → review mode
  useEffect(() => {
    if (phase !== "solving" || !deadline) return;
    if (now >= deadline) enterReview();
  }, [phase, deadline, now, enterReview]);

  // Poll submissions every 30s during solving/review
  useEffect(() => {
    if ((phase !== "solving" && phase !== "review") || !username || !currentProblem) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        setPollStatus("checking");
        const response = await fetch(`https://codeforces.com/api/user.status?handle=${username}&count=50`);
        const data = await response.json();
        if (cancelled) return;

        setLastPollAt(Date.now());

        if (data.status !== "OK") {
          setPollStatus("error");
          return;
        }

        const { contestId, index } = sessionRef.current.currentProblem || {};
        const accepted = (data.result || []).some(
          (sub) => sub.verdict === "OK" && sub.problem?.contestId === contestId && sub.problem?.index === index
        );

        setPollStatus(accepted ? "found" : "watching");
        if (accepted && advanceAfterAcceptRef.current) {
          await advanceAfterAcceptRef.current(Date.now());
        }
      } catch {
        if (!cancelled) setPollStatus("error");
      }
    };

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [phase, username, currentProblem]);

  const handleStart = async () => {
    setLoadingStart(true);
    setError(null);

    try {
      const infoResponse = await fetch(`https://codeforces.com/api/user.info?handles=${username}`);
      const infoData = await infoResponse.json();
      if (infoData.status !== "OK" || !infoData.result?.length) {
        throw new Error(infoData.comment || "Could not load your Codeforces rating");
      }

      const rating = infoData.result[0].rating || infoData.result[0].maxRating || 800;
      const startRating =
        selectedStartRating === "auto" ? roundRatingDown(rating) : clampRating(Number(selectedStartRating));
      const found = findNearestProblem(startRating, selectedTag, [], "both");

      if (!found) {
        throw new Error(
          selectedTag === "any"
            ? "No unsolved rated problems available to start training."
            : `No unsolved problems found for tag "${selectedTag}". Try another tag or Any.`
        );
      }

      setUserRating(rating);
      setHistory([]);
      setUsedKeys([]);
      setStreak(0);
      setBestStreak(0);
      setSolvedCount(0);
      setFailedCount(0);

      startRound(found.problem, found.rating, "solving", {
        usedKeys: [problemKey(found.problem)],
        history: [],
        streak: 0,
        bestStreak: 0,
        solvedCount: 0,
        failedCount: 0,
        selectedTag,
        userRating: rating,
      });
    } catch (err) {
      setError(err.message || "Failed to start training");
    } finally {
      setLoadingStart(false);
    }
  };

  const handleEndSession = () => {
    clearSession();
    setPhase("setup");
    setCurrentProblem(null);
    setDeadline(null);
    setHistory([]);
    setUsedKeys([]);
    setStreak(0);
    setError(null);
    setPollStatus("idle");
    advancingRef.current = false;
  };

  const problemUrl = currentProblem
    ? `https://codeforces.com/problemset/problem/${currentProblem.contestId}/${currentProblem.index}`
    : null;
  const tutorialUrl = currentProblem ? `https://codeforces.com/contest/${currentProblem.contestId}` : null;

  return (
    <div
      className={`min-h-screen font-['Inter'] transition-all duration-500 ${
        isDarkMode ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-slate-50 via-cyan-50/40 to-emerald-50/40"
      }`}
    >
      <header className={`backdrop-blur-xl border-b transition-all duration-500 ${isDarkMode ? "bg-slate-900/80 border-slate-700/50" : "bg-white/80 border-slate-200/50"}`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:justify-between lg:items-center py-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className={`p-3 rounded-2xl transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-gradient-to-br from-slate-700 to-slate-800 hover:from-slate-600 hover:to-slate-700 text-slate-300"
                    : "bg-gradient-to-br from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-600"
                }`}
                title="Back to Home"
              >
                <i className="fas fa-arrow-left text-xl"></i>
              </button>
              <div className="p-3 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-600">
                <i className="fas fa-dumbbell text-white text-xl"></i>
              </div>
              <div>
                <h1 className={`text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Training</h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                  Adaptive 30-minute drills for {username}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {(phase === "solving" || phase === "review") && (
                <button
                  onClick={handleEndSession}
                  className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                    isDarkMode ? "bg-red-500/15 text-red-300 border border-red-500/30" : "bg-red-50 text-red-600 border border-red-200"
                  }`}
                >
                  <i className="fas fa-stop mr-2"></i>
                  End Session
                </button>
              )}
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? "bg-slate-800/50 hover:bg-slate-700/50 text-slate-300" : "bg-slate-100/80 hover:bg-slate-200/80 text-slate-600"
                }`}
                title="Toggle dark mode"
              >
                <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        {error && (
          <div className={`mb-6 p-4 rounded-2xl ${isDarkMode ? "bg-red-500/10 text-red-300 border border-red-500/20" : "bg-red-50 text-red-700 border border-red-200"}`}>
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </div>
        )}

        {phase === "setup" && (
          <div className="grid lg:grid-cols-5 gap-8">
            <div
              className={`lg:col-span-3 rounded-3xl p-8 ${
                isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/90 border border-slate-200/60 shadow-xl"
              }`}
            >
              <div className="mb-8">
                <div className={`inline-flex items-center px-4 py-2 rounded-full mb-4 ${isDarkMode ? "bg-cyan-500/10 text-cyan-300" : "bg-cyan-50 text-cyan-700"}`}>
                  <i className="fas fa-bolt mr-2"></i>
                  Adaptive ladder
                </div>
                <h2 className={`text-3xl font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Start a training session</h2>
                <p className={`text-lg ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                  Pick a tag and optional start rating. Default starts near your CF rating, then climbs or drops by 100.
                </p>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Starting rating</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => setSelectedStartRating("auto")}
                    className={`px-4 py-2 rounded-xl font-medium transition-all ${
                      selectedStartRating === "auto"
                        ? "bg-gradient-to-r from-cyan-500 to-emerald-600 text-white shadow-lg"
                        : isDarkMode
                        ? "bg-slate-700/60 text-slate-200 hover:bg-slate-700"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    Auto (my rating)
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto pr-1">
                  {RATING_OPTIONS.map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setSelectedStartRating(rating)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${
                        selectedStartRating === rating
                          ? `${getRatingBadge(rating)} ring-2 ring-offset-2 ${isDarkMode ? "ring-cyan-400 ring-offset-slate-800" : "ring-cyan-500 ring-offset-white"}`
                          : isDarkMode
                          ? "bg-slate-700/50 text-slate-300 hover:bg-slate-700"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <p className={`text-xs mt-3 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                  {selectedStartRating === "auto"
                    ? "Uses your current rating floored to the nearest 100 (e.g. 1763 → 1700)."
                    : `Session will start at ${selectedStartRating}.`}
                </p>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Topic focus</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setSelectedTag("any")}
                    className={`px-4 py-2 rounded-xl font-medium transition-all ${
                      selectedTag === "any"
                        ? "bg-gradient-to-r from-cyan-500 to-emerald-600 text-white shadow-lg"
                        : isDarkMode
                        ? "bg-slate-700/60 text-slate-200 hover:bg-slate-700"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    Any topic
                  </button>
                </div>

                <input
                  type="text"
                  value={tagSearch}
                  onChange={(e) => setTagSearch(e.target.value)}
                  placeholder="Search tags..."
                  className={`w-full px-4 py-3 rounded-xl border-2 mb-4 outline-none transition-all focus:ring-4 focus:ring-cyan-500/20 focus:border-cyan-500 ${
                    isDarkMode ? "bg-slate-900/60 border-slate-600/50 text-slate-100 placeholder-slate-500" : "bg-white border-slate-200 text-slate-900"
                  }`}
                />

                <div className="flex flex-wrap gap-2 max-h-56 overflow-y-auto pr-1">
                  {filteredTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(tag)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedTag === tag
                          ? "bg-gradient-to-r from-cyan-500 to-emerald-600 text-white"
                          : isDarkMode
                          ? "bg-slate-700/50 text-slate-300 hover:bg-slate-700"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleStart}
                disabled={loadingStart || !problemsWithStats?.length}
                className="w-full py-4 rounded-2xl font-bold text-lg bg-gradient-to-r from-cyan-500 to-emerald-600 text-white shadow-xl hover:scale-[1.01] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingStart ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Preparing first problem...
                  </>
                ) : (
                  <>
                    <i className="fas fa-play mr-2"></i>
                    Start Training
                  </>
                )}
              </button>
            </div>

            <div className="lg:col-span-2 space-y-4">
              {[
                { icon: "fa-gauge-high", title: "Flexible start", text: "Auto-match your rating, or pick any start from 800–3500." },
                { icon: "fa-clock", title: "30 minute rounds", text: "Solve in time to climb +100. Miss it and review the solution." },
                { icon: "fa-arrows-up-down", title: "Adaptive ladder", text: "AC climbs. After a review submit, difficulty drops −100." },
                { icon: "fa-chart-simple", title: "Easier picks", text: "Within a rating, prefers high solve-count / solve-rate problems. Never reuses solved ones." },
                { icon: "fa-rotate", title: "Live sync", text: "We poll your Codeforces submissions every 30 seconds." },
              ].map((card) => (
                <div
                  key={card.title}
                  className={`rounded-2xl p-5 ${isDarkMode ? "bg-slate-800/50 border border-slate-700/40" : "bg-white/80 border border-slate-200/60 shadow"}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-600 text-white">
                      <i className={`fas ${card.icon}`}></i>
                    </div>
                    <div>
                      <h3 className={`font-bold mb-1 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{card.title}</h3>
                      <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>{card.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(phase === "solving" || phase === "review") && currentProblem && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { label: "Solved", value: solvedCount, icon: "fa-check" },
                { label: "Reviewed", value: failedCount, icon: "fa-book-open" },
                { label: "Streak", value: streak, icon: "fa-fire" },
                { label: "Best", value: bestStreak, icon: "fa-trophy" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className={`rounded-2xl p-5 ${isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow"}`}
                >
                  <div className={`text-sm mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    <i className={`fas ${stat.icon} mr-2`}></i>
                    {stat.label}
                  </div>
                  <div className={`text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{stat.value}</div>
                </div>
              ))}
            </div>

            <div
              className={`rounded-3xl overflow-hidden ${
                isDarkMode ? "bg-slate-800/60 border border-slate-700/50 shadow-2xl" : "bg-white/90 border border-slate-200/60 shadow-xl"
              }`}
            >
              <div
                className={`px-8 py-6 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${
                  phase === "review"
                    ? isDarkMode
                      ? "bg-amber-500/10 border-amber-500/20"
                      : "bg-amber-50 border-amber-200"
                    : isDarkMode
                    ? "border-slate-700/50 bg-gradient-to-r from-slate-800/80 to-slate-900/80"
                    : "border-slate-200/50 bg-gradient-to-r from-cyan-50/80 to-emerald-50/80"
                }`}
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        phase === "review"
                          ? isDarkMode
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-amber-100 text-amber-700"
                          : isDarkMode
                          ? "bg-cyan-500/20 text-cyan-300"
                          : "bg-cyan-100 text-cyan-700"
                      }`}
                    >
                      {phase === "review" ? "Solution review" : "Solve window"}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getRatingBadge(currentProblem.rating || targetRating)}`}>
                      {currentProblem.rating || targetRating}
                    </span>
                  </div>
                  <h2 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                    {currentProblem.contestId}
                    {currentProblem.index}. {currentProblem.name}
                  </h2>
                  <p className={`text-sm mt-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {phase === "review"
                      ? "Time ran out — study the editorial, then submit an accepted solution to continue at −100."
                      : "Submit an accepted solution before the timer ends to climb +100."}
                  </p>
                </div>

                <div className="text-center lg:text-right">
                  <div
                    className={`font-mono text-5xl font-bold tracking-tight ${
                      timerUrgent ? "text-red-500 animate-pulse" : isDarkMode ? "text-cyan-300" : "text-cyan-700"
                    }`}
                  >
                    {formatTime(remainingMs)}
                  </div>
                  <div className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {pickingNext ? "Loading next problem..." : pollStatus === "checking" ? "Checking submissions..." : "Auto-check every 30s"}
                  </div>
                  {lastPollAt && (
                    <div className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                      Last check {Math.max(0, Math.floor((Date.now() - lastPollAt) / 1000))}s ago
                    </div>
                  )}
                </div>
              </div>

              <div className="p-8">
                <div className="flex flex-wrap gap-2 mb-6">
                  {(currentProblem.tags || []).map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 rounded-lg text-sm ${isDarkMode ? "bg-slate-700/60 text-slate-300" : "bg-slate-100 text-slate-700"}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <a
                    href={problemUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="px-5 py-3 rounded-xl font-semibold bg-gradient-to-r from-cyan-500 to-emerald-600 text-white shadow-lg hover:scale-105 transition-all"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    Open Problem
                  </a>
                  {phase === "review" && (
                    <a
                      href={tutorialUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={`px-5 py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
                        isDarkMode ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      <i className="fas fa-lightbulb mr-2"></i>
                      Contest / Editorial
                    </a>
                  )}
                  <button
                    onClick={() => navigate(`/problem/${currentProblem.contestId}/${currentProblem.index}`)}
                    className={`px-5 py-3 rounded-xl font-semibold transition-all hover:scale-105 ${
                      isDarkMode ? "bg-slate-700/70 text-slate-200" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    <i className="fas fa-info-circle mr-2"></i>
                    Local Details
                  </button>
                </div>

                {phase === "review" && remainingMs <= 0 && (
                  <div className={`mt-6 p-4 rounded-2xl ${isDarkMode ? "bg-amber-500/10 text-amber-200" : "bg-amber-50 text-amber-800"}`}>
                    <i className="fas fa-hourglass-end mr-2"></i>
                    Review timer finished — still waiting for your accepted submission before dropping −100 and continuing.
                  </div>
                )}
              </div>
            </div>

            {history.length > 0 && (
              <div className={`rounded-3xl p-6 ${isDarkMode ? "bg-slate-800/50 border border-slate-700/40" : "bg-white/80 border border-slate-200/60"}`}>
                <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Session history</h3>
                <div className="space-y-2">
                  {history.slice(0, 8).map((item) => (
                    <div key={`${item.key}-${item.at}`} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <span className={`font-medium truncate block ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                          {item.contestId}
                          {item.index}. {item.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${getRatingBadge(item.rating)}`}>{item.rating}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-semibold ${
                            item.result === "solved"
                              ? isDarkMode
                                ? "bg-emerald-500/20 text-emerald-300"
                                : "bg-emerald-50 text-emerald-700"
                              : isDarkMode
                              ? "bg-amber-500/20 text-amber-300"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {item.result === "solved" ? "+100" : "−100"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {phase === "complete" && (
          <div
            className={`max-w-2xl mx-auto text-center rounded-3xl p-10 ${
              isDarkMode ? "bg-slate-800/60 border border-slate-700/50" : "bg-white/90 border border-slate-200/60 shadow-xl"
            }`}
          >
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-cyan-500 to-emerald-600 text-white mb-6">
              <i className="fas fa-flag-checkered text-3xl"></i>
            </div>
            <h2 className={`text-3xl font-bold mb-3 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>No more problems in range</h2>
            <p className={`mb-8 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
              Session stats — solved {solvedCount}, reviewed {failedCount}, best streak {bestStreak}.
            </p>
            <button
              onClick={handleEndSession}
              className="px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-cyan-500 to-emerald-600 text-white hover:scale-105 transition-all"
            >
              Back to setup
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default Training;
